import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const nm = (p) => `${p.first_name || ""} ${p.last_name || ""}`.trim();
const val = (v) => (Array.isArray(v) ? v.join(", ") : v) || "—";

function PersonPicker({ selfId, onPick }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  useEffect(() => {
    if (q.trim().length < 2) { setRes([]); return; }
    const t = setTimeout(() => {
      api.get("/persons", { params: { q: q.trim(), page_size: 10 } }).then((r) => setRes((r.data.persons || []).filter((p) => p.id !== selfId))).catch(() => setRes([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q, selfId]);
  return (
    <div className="space-y-2">
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, email or P-number" data-testid="combine-search" />
      <div className="max-h-56 overflow-y-auto divide-y divide-slate-100 rounded-md border border-slate-200">
        {res.map((p) => (
          <button key={p.id} type="button" onClick={() => onPick(p)} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50" data-testid={`combine-pick-${p.human_id}`}>
            <span className="font-medium">{nm(p)}</span> <span className="text-xs text-slate-400">{p.human_id} · {p.email || "no email"}</span>
          </button>))}
        {q.trim().length >= 2 && !res.length && <p className="px-3 py-2 text-xs text-slate-400">No other matching people</p>}
      </div>
    </div>
  );
}

function Choice({ label, testid, checked, onChange, children }) {
  return <label className="flex items-start gap-2 text-sm cursor-pointer"><input type="radio" checked={checked} onChange={onChange} className="mt-1" data-testid={testid} /><span><span className="font-medium">{label}</span> {children}</span></label>;
}

export function CombinePersonDialog({ person, initialOther = null, classTypes, onClose, onDone }) {
  const [other, setOther] = useState(initialOther);
  const [keepSelf, setKeepSelf] = useState(true);
  const [prev, setPrev] = useState(null);
  const [res, setRes] = useState({});
  const [cls, setCls] = useState([]);
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const surv = keepSelf ? person : other;
  const dup = keepSelf ? other : person;
  const allCls = [...new Set([...(person.classifications || []), ...((other && other.classifications) || [])])];
  const label = (k) => classTypes.find((c) => c.key === k)?.label || k;
  useEffect(() => { if (other) { api.get(`/persons/${other.id}`).then((r) => { setOther((o) => ({ ...o, classifications: r.data.classifications || [] })); setCls([...new Set([...(person.classifications || []), ...(r.data.classifications || [])])]); }).catch(() => {}); } }, [other?.id]); // eslint-disable-line
  useEffect(() => {
    if (!other) { return; }
    setPrev(null); setRes({});
    api.post("/records/persons/merge-preview", { survivor_id: surv.id, duplicate_id: dup.id }).then((r) => setPrev(r.data)).catch((e) => { toast.error(apiError(e)); setOther(null); });
  }, [other?.id, keepSelf]); // eslint-disable-line
  const go = async () => {
    setBusy(true);
    try {
      await api.post("/records/persons/merge", { survivor_id: surv.id, duplicate_id: dup.id, field_resolutions: res, acknowledge: true, keep_classifications: cls });
      toast.success(`Combined into ${nm(surv)} (${surv.human_id})`);
      onDone(surv.id);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="combine-dialog">
        <DialogHeader><DialogTitle>Combine {nm(person)} ({person.human_id}) with another record</DialogTitle>
          <DialogDescription>Activities, tasks, interviews, enrollments and files move to the record you keep. The other record is archived, not deleted, and its name is saved as a former name.</DialogDescription></DialogHeader>
        {!other ? <PersonPicker selfId={person.id} onPick={setOther} /> : (
          <div className="space-y-4">
            <div><p className="text-xs font-semibold text-slate-600 mb-1">Record to keep</p>
              <div className="space-y-1">
                <Choice label={`${nm(person)} (${person.human_id})`} testid="combine-keep-self" checked={keepSelf} onChange={() => setKeepSelf(true)}>this record</Choice>
                <Choice label={`${nm(other)} (${other.human_id})`} testid="combine-keep-other" checked={!keepSelf} onChange={() => setKeepSelf(false)} />
              </div>
              <button type="button" className="text-xs text-[#006090] mt-1" onClick={() => { setOther(null); setKeepSelf(true); }} data-testid="combine-change">Choose a different record</button></div>
            {prev?.conflicts?.length > 0 && <div data-testid="combine-conflicts"><p className="text-xs font-semibold text-slate-600 mb-1">Different values — choose which to keep</p>
              {prev.conflicts.map((f) => (
                <div key={f} className="grid grid-cols-[110px_1fr_1fr] gap-2 items-start py-1 border-t border-slate-100 text-sm">
                  <span className="text-xs text-slate-500 capitalize">{f.replace(/_/g, " ")}</span>
                  <Choice label={val(prev.survivor.fields[f])} testid={`combine-field-${f}-survivor`} checked={(res[f] || "survivor") === "survivor"} onChange={() => setRes({ ...res, [f]: "survivor" })} />
                  <Choice label={val(prev.duplicate.fields[f])} testid={`combine-field-${f}-duplicate`} checked={res[f] === "duplicate"} onChange={() => setRes({ ...res, [f]: "duplicate" })} />
                </div>))}</div>}
            <div><p className="text-xs font-semibold text-slate-600 mb-1">Classifications to keep</p>
              <div className="flex flex-wrap gap-3">{allCls.map((c) => (
                <label key={c} className="flex items-center gap-1.5 text-sm"><input type="checkbox" checked={cls.includes(c)} onChange={(e) => setCls(e.target.checked ? [...cls, c] : cls.filter((x) => x !== c))} data-testid={`combine-cls-${c}`} />{label(c)}</label>))}</div></div>
            <label className="flex items-start gap-2 text-sm"><input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-1" data-testid="combine-ack" />
              I've confirmed these are the same person. {dup && `${nm(dup)} (${dup.human_id}) will be archived into ${nm(surv)} (${surv.human_id}).`}</label>
          </div>)}
        <DialogFooter><Button variant="outline" onClick={onClose} data-testid="combine-cancel">Cancel</Button>
          <Button onClick={go} disabled={busy || !other || !prev || !ack || !cls.length} className="bg-[#006090] hover:bg-[#004E75]" data-testid="combine-confirm">Combine records</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
